import moment from "moment";

export function getFRA(birthYear) {
  if (birthYear <= 1954) return 66.0;
  if (birthYear === 1955) return 66 + 2 / 12;
  if (birthYear === 1956) return 66 + 4 / 12;
  if (birthYear === 1957) return 66 + 6 / 12;
  if (birthYear === 1958) return 66 + 8 / 12;
  if (birthYear === 1959) return 66 + 10 / 12;
  return 67.0;
}

export function adjustForClaimAge(pia, claimAge, fra) {
  const monthsDiff = Math.round((claimAge - fra) * 12);
  if (monthsDiff < 0) {
    // early claiming reduction
    const monthsEarly = -monthsDiff;
    const reductionFirst36 = Math.min(36, monthsEarly) * (5 / 9 / 100);
    const reductionAfter36 = Math.max(0, monthsEarly - 36) * (5 / 12 / 100);
    return pia * (1 - (reductionFirst36 + reductionAfter36));
  } else {
    // delayed credits
    const delayedCredits = monthsDiff * (2 / 3 / 100);
    return pia * (1 + delayedCredits);
  }
}

export function calcSpousalPortion(primaryPIA, claimAge, spouseFRA) {
  const maxSpousal = primaryPIA * 0.5;
  const monthsDiff = Math.round((claimAge - spouseFRA) * 12);
  if (monthsDiff < 0) {
    const monthsEarly = -monthsDiff;
    const reductionFirst36 = Math.min(36, monthsEarly) * (25 / 36 / 100);
    const reductionAfter36 = Math.max(0, monthsEarly - 36) * (5 / 12 / 100);
    return maxSpousal * (1 - (reductionFirst36 + reductionAfter36));
  }
  return maxSpousal;
}

export function calculateTaxableBenefit(
  userBenefit,
  spouseBenefit,
  otherIncome,
  taxStatus
) {
  const totalSS = (userBenefit + spouseBenefit) * 12;
  const provisionalIncome = otherIncome + 0.5 * totalSS;

  let t1 = 0,
    t2 = 0;
  switch (taxStatus) {
    case "Single":
    case "Head of Household":
    case "Qualifying Widow(er)":
    case "Married Filing Separately (lived apart)":
      t1 = 25000;
      t2 = 34000;
      break;
    case "Married Filing Jointly":
      t1 = 32000;
      t2 = 44000;
      break;
    case "Married Filing Separately (lived together)":
      return (totalSS * 0.85) / 12;
    default:
      t1 = 25000;
      t2 = 34000;
  }

  let taxable = 0;
  if (provisionalIncome > t2) {
    taxable = Math.min(
      totalSS * 0.85,
      (provisionalIncome - t2) * 0.85 + (t2 - t1) * 0.5
    );
  } else if (provisionalIncome > t1) {
    taxable = (provisionalIncome - t1) * 0.5;
  }

  return (taxable * 0.22) / 12; // monthly
}

export function calculateLifetimePV(
  inputs,
  userBenefit,
  spouseBenefit,
  userClaimAge,
  spouseClaimAge,
  userFRA,
  spouseFRA
) {
  let totalPV = 0;
  const r = inputs.financial.tvmRate;
  const startAge = Math.min(userClaimAge, spouseClaimAge);
  const firstDeathAge = Math.min(inputs.user.yod, inputs.spouse.yod);
  const survivorEndAge = Math.max(inputs.user.yod, inputs.spouse.yod);

  for (let age = startAge; age < survivorEndAge; age++) {
    let annual = 0;
    if (age < firstDeathAge) {
      if (age >= userClaimAge) annual += userBenefit * 12;
      if (age >= spouseClaimAge) annual += spouseBenefit * 12;
    } else {
      const survivor = Math.max(
        adjustForClaimAge(inputs.user.pia, userClaimAge, userFRA),
        adjustForClaimAge(inputs.spouse.pia, spouseClaimAge, spouseFRA)
      );
      annual = survivor * 12;
    }
    const discountYear = age - startAge + 1;
    totalPV += annual / Math.pow(1 + r, discountYear);
  }
  return totalPV;
}

export function calculateBreakeven(inputs, userFRA) {
  const benefitAt62 = adjustForClaimAge(inputs.user.pia, 62, userFRA);
  const benefitAtFRA = inputs.user.pia;
  let cum62 = 0,
    cumFRA = 0;
  for (let age = 62; age <= inputs.user.yod; age++) {
    cum62 += benefitAt62 * 12;
    if (age >= userFRA) cumFRA += benefitAtFRA * 12;
    if (cumFRA > cum62 && cumFRA > 0) return age;
  }
  return "--";
}

export const calculateSocialSecurityResults = (formData) => {
  if (!formData || !formData["Your Birthdate"]) return;

  const inputs = {
    user: {
      dob: formData["Your Birthdate"],
      pia: Number(formData["Your Monthly Benefit at FRA ($)"]) || 0,
      yod: Number(formData["Your Estimated Year of Death"]) || 0,
      claimAge: Number(formData["Your Desired Claim Age"]) || 62,
    },
    spouse: {
      dob: formData["Spouse Birthdate"],
      pia: Number(formData["Spouse Monthly Benefit at FRA ($)"]) || 0,
      yod: Number(formData["Spouse Estimated Year of Death"]) || 0,
      claimAge: Number(formData["Spouse Desired Claim Age"]) || 62,
    },
    financial: {
      taxStatus: formData["Tax Filing Status"] || "Single",
      otherIncome: Number(formData["Other Annual Retirement Income ($)"]) || 0,
      tvmRate: (Number(formData["Discount Rate (TVM, %)"]) || 0) / 100,
    },
    special: {
      hasWEP: formData["Eligible for WEP/GPO?"] || false,
      wepAmount: Number(formData["WEP/GPO Pension Amount ($)"]) || 0,
      priorMarriage10Plus:
        formData["Were you in a prior marriage lasting 10+ years?"] || false,
      exSpouseBenefit:
        Number(formData["Ex-Spouse Estimated Monthly Benefit ($)"]) || 0,
      hasMilitaryCredits:
        formData["Do you have military service credits (1957-2001)?"] || false,
    },
  };

  const userFRA = getFRA(new Date(inputs.user.dob).getFullYear());
  const spouseFRA = getFRA(new Date(inputs.spouse.dob).getFullYear());

  let userBenefit = adjustForClaimAge(
    inputs.user.pia,
    inputs.user.claimAge,
    userFRA
  );
  let spouseBenefit = adjustForClaimAge(
    inputs.spouse.pia,
    inputs.spouse.claimAge,
    spouseFRA
  );
  const combined = userBenefit + spouseBenefit;

  const taxable = calculateTaxableBenefit(
    userBenefit,
    spouseBenefit,
    inputs.financial.otherIncome,
    inputs.financial.taxStatus
  );
  const lifetime = calculateLifetimePV(
    inputs,
    userBenefit,
    spouseBenefit,
    inputs.user.claimAge,
    inputs.spouse.claimAge,
    userFRA,
    spouseFRA
  );
  const survivor = Math.max(userBenefit, spouseBenefit);
  const breakeven = calculateBreakeven(inputs, userFRA);

  // FRA scenario
  let userFRA_benefit = adjustForClaimAge(inputs.user.pia, userFRA, userFRA);
  let spouseFRA_own = adjustForClaimAge(
    inputs.spouse.pia,
    spouseFRA,
    spouseFRA
  );
  let spouseFRA_spousal = calcSpousalPortion(
    inputs.user.pia,
    spouseFRA,
    spouseFRA
  );

  // if WEP applies, adjust benefits
  if (inputs.special.hasWEP) {
    userFRA_benefit = Math.max(
      0,
      userFRA_benefit - inputs.special.wepAmount * 0.5
    );
    spouseFRA_spousal = Math.max(
      0,
      spouseFRA_spousal - (inputs.special.wepAmount * 2) / 3
    );
  }

  const spouseFRA_total = Math.max(spouseFRA_own, spouseFRA_spousal);

  const fraCombined = userFRA_benefit + spouseFRA_total;
  const fraTaxable = calculateTaxableBenefit(
    userFRA_benefit,
    spouseFRA_total,
    inputs.financial.otherIncome,
    inputs.financial.taxStatus
  );
  const lifetimeFRA_PV = calculateLifetimePV(
    inputs,
    userFRA_benefit,
    spouseFRA_total,
    userFRA,
    spouseFRA,
    userFRA,
    spouseFRA
  );
  const fraSurvivorBenefit = Math.max(userFRA_benefit, spouseFRA_own);

  const userBirth = moment(formData["Your Birthdate"], "MM/DD/YYYY").toDate();
  const userYear = userBirth.getFullYear();

  // FRA (years + months)
  const fraObj = getFRA(userYear);
  const fraYears = typeof fraObj === "object" ? fraObj.years : fraObj;
  const fraMonths = typeof fraObj === "object" ? fraObj.months : 0;

  // FRA Date
  const fraDate = new Date(userBirth);
  fraDate.setFullYear(fraDate.getFullYear() + fraYears);
  fraDate.setMonth(fraDate.getMonth() + fraMonths);

  // Time until FRA
  let fraCountdown = "N/A";
  if (!isNaN(fraDate)) {
    const now = new Date();
    let diffYears = fraDate.getFullYear() - now.getFullYear();
    let diffMonths = fraDate.getMonth() - now.getMonth();
    let diffDays = fraDate.getDate() - now.getDate();

    if (diffDays < 0) {
      diffMonths -= 1;
      diffDays += new Date(
        fraDate.getFullYear(),
        fraDate.getMonth(),
        0
      ).getDate();
    }
    if (diffMonths < 0) {
      diffYears -= 1;
      diffMonths += 12;
    }

    fraCountdown = `${diffYears} Years ${diffMonths} Months ${diffDays} Days`;
  }

  const fraResults = {
    fraAge: `${fraYears} Years and ${fraMonths} Months`,
    fraDate: !isNaN(fraDate) ? fraDate.toLocaleDateString("en-US") : "N/A",
    fraCountdown,
  };

  return {
    // Desired
    userBenefit,
    spouseBenefit,
    combined,
    taxable,
    lifetime,
    survivor,
    breakeven,

    // FRA scenario
    userFRA_benefit,
    spouseFRA_total,
    fraCombined,
    fraTaxable,
    lifetimeFRA_PV,
    fraSurvivorBenefit,
    fraResults,
  };
};
